import React from 'react';

import { ErrorCodes } from '@aline/common';
import { link } from '@aline/routes';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import type { GraphQLFormattedError } from 'graphql';
import { redirectToLogin } from 'src/auth';
import { sessionTimeOutModalVar } from 'src/components/auth/SessionTimeOutModal';
import { UpgradeRequiredSnackbar } from 'src/components/snackbars/UpgradeRequiredSnackbar';
import { namedOperations } from 'src/gql/generated/apollo-types';
import { redirectTo } from 'src/helpers';
import { pushNotification } from 'src/notifications';
import { getErrorCode, handleError } from 'src/store/errors';
import { v4 as uuid4 } from 'uuid';

import {
  getIsIgnoredOperation,
  getIsSelfReportedOperation,
  getShouldHandleError,
} from './error-link-policy';

export interface OperationErrorContext {
  genericErrorMessage?: string;
  onErrorHandled?: () => void;
}

// Operations whose views show their own alert for a CRM record deleted in the CRM.
const CRM_RECORD_NOT_FOUND_HANDLED_OPERATIONS: readonly string[] = [
  namedOperations.Query.GetOpportunity,
];

/**
 * Error middleware. Warns, then routes each code to a redirect, a toast, or
 * `handleError`. A caller can pass `genericErrorMessage` so one action shows
 * a single message, and `onErrorHandled` so it does not toast again itself.
 */
export const handleApolloError: ConstructorParameters<typeof ErrorLink>[0] = ({
  error,
  operation,
}) => {
  const context = operation.getContext();
  const genericMessage =
    typeof context.genericErrorMessage === 'string' ? context.genericErrorMessage : undefined;
  const markHandled = () => {
    const onErrorHandled = context.onErrorHandled;
    if (typeof onErrorHandled === 'function') onErrorHandled();
  };

  if (!CombinedGraphQLErrors.is(error)) {
    markHandled();
    if (ServerError.is(error) && error.statusCode === 401) {
      sessionTimeOutModalVar(true);
      return;
    }
    console.warn(`[Network error]: ${error}.`);
    pushNotification({
      key: uuid4(),
      message: genericMessage ?? 'A network error occurred. Try your action again.',
      variant: 'error',
    });
    return;
  }

  const isIgnoredOperation = getIsIgnoredOperation(operation.operationName);
  const isSelfReportedOperation = getIsSelfReportedOperation(operation.operationName);
  const actionableErrors = error.errors.filter(gqlError =>
    getShouldHandleError({
      code: getErrorCode(gqlError),
      isIgnoredOperation,
      isSelfReportedOperation,
    })
  );
  /**
   * Not-found is left to the surface. Counting it as handled would suppress a
   * caller that still needs to show its own message.
   */
  const surfacedErrors = actionableErrors.filter(
    gqlError => getErrorCode(gqlError) !== ErrorCodes.NOT_FOUND
  );
  if (!surfacedErrors.length) return;

  markHandled();
  const primaryError = genericMessage ? getPrimaryError(surfacedErrors) : undefined;
  for (const gqlError of actionableErrors) {
    const { locations, message, path } = gqlError;
    console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}.`);
    if (!primaryError || gqlError === primaryError)
      notifyGraphQLError({ genericMessage, gqlError, operationName: operation.operationName });
    else if (getErrorCode(gqlError) === ErrorCodes.INTERNAL)
      handleError(gqlError, { notify: false });
  }
};

export const errorLink = new ErrorLink(handleApolloError);

/** The error a generic-message action reports: a session problem first, then any specific one. */
function getPrimaryError(errors: readonly GraphQLFormattedError[]) {
  return (
    errors.find(gqlError => {
      const code = getErrorCode(gqlError);
      return code === ErrorCodes.UNAUTHENTICATED || code === ErrorCodes.USER_DEACTIVATED_ERROR;
    }) ??
    errors.find(gqlError => getErrorCode(gqlError) !== ErrorCodes.INTERNAL) ??
    errors.at(0)
  );
}

function notifyGraphQLError({
  genericMessage,
  gqlError,
  operationName,
}: {
  genericMessage: string | undefined;
  gqlError: GraphQLFormattedError;
  operationName: string | undefined;
}) {
  switch (getErrorCode(gqlError)) {
    case ErrorCodes.USER_DEACTIVATED_ERROR:
      redirectTo(link.Deactivated());
      return;
    case ErrorCodes.BAD_USER_INPUT:
      pushNotification({ key: uuid4(), message: gqlError.message, variant: 'error' });
      return;
    case ErrorCodes.UNAUTHORIZED:
      pushNotification({
        key: uuid4(),
        message: 'Not authorized to perform requested action.',
        variant: 'error',
      });
      return;
    case ErrorCodes.UPGRADE_REQUIRED: {
      const id = uuid4();
      pushNotification({
        content: React.createElement(UpgradeRequiredSnackbar, { id, message: gqlError.message }),
        key: id,
        message: '',
        variant: 'warning',
      });
      return;
    }
    case ErrorCodes.CRM_RECORD_NOT_FOUND:
      if (!CRM_RECORD_NOT_FOUND_HANDLED_OPERATIONS.includes(operationName ?? ''))
        pushNotification({ key: uuid4(), message: gqlError.message, variant: 'warning' });
      return;
    /**
     * A missing record is an answer, not a failure: the surface that asked
     * renders its own empty or not-found state, so neither the generic
     * snackbar nor a Sentry report adds anything.
     */
    case ErrorCodes.NOT_FOUND:
      return;
    case ErrorCodes.UNAUTHENTICATED:
      redirectToLogin();
      return;
    default:
      handleError(gqlError, { message: genericMessage });
  }
}
